import ActionCard from "@/components/ActionCard";
import type { MaterialNames } from "@/components/material_symbol_names";
import {
  useCanManageApplications,
  useCanManageCommitteesBool,
  useCanViewCommitteesBool
} from "@/hooks/useCapabilities";
import { useFlags } from "@/hooks/useFeatureFlags";
import usePageTitle from "@/hooks/usePageTitle";
import { isApplicationsMembershipEnabled } from "@/pages/people/_membershipServicesNavLinks";
import { COMMITTEES_BASE_PATH } from "@/pages/people/membership-services/committees/constants";
import {
  GROUP_EMAIL_PAGE_PATH,
  useCanAccessGroupEmailPage
} from "@/pages/people/membership-services/group-email/access";
import {
  PBD_CARD_MANUAL_FULFILLMENT_PATH,
  usePbdCardManualFulfillmentAccess
} from "@/pages/people/membership-services/pbd-card-manual-fulfillment/access";
import type { Path } from "@/router";
import { useNavigate } from "@/router";
import TabLayout from "../_TabLayout";

type MembershipServiceCard = {
  title: string;
  description: string;
  iconName: MaterialNames;
  to: Path;
};

const APPLICATIONS_CARD: MembershipServiceCard = {
  title: "Manage Applications",
  description: "Review and process all types of membership program applications",
  iconName: "assignment_ind",
  to: "/people/membership-services/applications/all"
};

const MEMBER_ELECTIONS_CARD: MembershipServiceCard = {
  title: "Manage Member Elections",
  description: "View people who are ready to be elected to membership and manually process the elections",
  iconName: "badge",
  to: "/people/membership-services/member-elections"
};

const ACHIEVEMENTS_REPORT_CARD: MembershipServiceCard = {
  title: "View Achievements",
  description: "View a list of awards and certifications earned by people",
  iconName: "emoji_events",
  to: "/people/membership-services/achievements-report"
};

const PBD_CARD_MANUAL_FULFILLMENT_CARD: MembershipServiceCard = {
  title: "PBD Card Manual Fulfillment",
  description: "Queue an ad hoc PBD credential request for Associate and Member cards",
  iconName: "badge",
  to: PBD_CARD_MANUAL_FULFILLMENT_PATH
};

const GROUP_EMAIL_CARD: MembershipServiceCard = {
  title: "Group Email",
  description: "Create group emails for committees or section governance",
  iconName: "mail",
  to: GROUP_EMAIL_PAGE_PATH
};

export default function MembershipServicesPage() {
  usePageTitle("/people/membership-services", "People | Operations");
  const navigate = useNavigate();
  const flags = useFlags();
  const canManageApplications = useCanManageApplications();
  const canViewCommittees = useCanViewCommitteesBool();
  const canManageCommittees = useCanManageCommitteesBool();
  const { canQueue: canQueuePbdCardManualFulfillment } = usePbdCardManualFulfillmentAccess();
  const { canAccess: canAccessGroupEmailPage } = useCanAccessGroupEmailPage();

  const showManageCommitteesCopy = canManageCommittees;
  const committeeManagementCard: MembershipServiceCard = {
    title: showManageCommitteesCopy ? "Manage Committees & Governance" : "View Committees & Governance",
    description: showManageCommitteesCopy
      ? "Manage National committees, committee participants, and National Governance"
      : "View National committees, committee participants, and National Governance",
    iconName: "groups",
    to: COMMITTEES_BASE_PATH
  };

  const cards = [
    ...(canManageApplications ? [APPLICATIONS_CARD] : []),
    ACHIEVEMENTS_REPORT_CARD,
    ...(canManageApplications && isApplicationsMembershipEnabled(flags) ? [MEMBER_ELECTIONS_CARD] : []),
    ...(canQueuePbdCardManualFulfillment ? [PBD_CARD_MANUAL_FULFILLMENT_CARD] : []),
    ...(canAccessGroupEmailPage ? [GROUP_EMAIL_CARD] : []),
    ...(canViewCommittees ? [committeeManagementCard] : [])
  ];

  return (
    <TabLayout>
      <div className="w-full">
        <p className="text-body-medium mb-6 mt-12">Select an operations area below to get started.</p>
        <div className="grid w-full grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="operations-cards">
          {cards.map((card) => (
            <div key={card.title} className="min-w-0">
              <ActionCard
                actionFn={() => navigate(card.to)}
                iconName={card.iconName}
                text={card.description}
                title={card.title}
                className="w-full max-w-none"
              />
            </div>
          ))}
        </div>
      </div>
    </TabLayout>
  );
}
